import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, Loader2, Check } from 'lucide-react';
import { createCertificationType, type StaffCertificationType } from '../../services/staffCertification';

interface CertificationTypeComboboxProps {
  /** The org type catalog, owned by the parent. */
  types: StaffCertificationType[];
  /** Currently selected certification type id ('' = none). */
  value: number | '';
  onChange: (id: number) => void;
  /** Called after a new type is created so the parent can merge it into its catalog. */
  onTypeCreated: (type: StaffCertificationType) => void;
  /** Whether the inline "+ Add" affordance is offered. HR-only — creating types
   *  requires the timesheet.hr seat, which the backend enforces. */
  canAddType?: boolean;
  disabled?: boolean;
}

const typeLabel = (t: StaffCertificationType) =>
  `${t.category ? `${t.category} — ` : ''}${t.name}${t.is_required ? ' (required)' : ''}`;

/**
 * Certification-type picker with an inline "+ Add" affordance. Mirrors the
 * default-list-plus-add-new pattern used by CropTypeCombobox: type to filter the
 * existing catalog, and when the typed text matches no existing type a button
 * appears to create it (POST /certifications/types) and select it in one step.
 */
function CertificationTypeCombobox({
  types,
  value,
  onChange,
  onTypeCreated,
  canAddType = false,
  disabled,
}: CertificationTypeComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const selected = types.find(t => t.id === value);

  const sorted = [...types].sort((a, b) => typeLabel(a).localeCompare(typeLabel(b)));
  const filtered = search.trim()
    ? sorted.filter(t => typeLabel(t).toLowerCase().includes(search.toLowerCase()))
    : sorted;

  const exactMatch = types.some(
    t => t.name.toLowerCase() === search.trim().toLowerCase(),
  );
  const showAddNew = canAddType && search.trim().length > 0 && !exactMatch && !adding;

  const handleSelect = (t: StaffCertificationType) => {
    onChange(t.id);
    setOpen(false);
    setSearch('');
  };

  const handleAddNew = async () => {
    const name = search.trim();
    if (!name) return;
    setAdding(true);
    setAddError(null);
    try {
      const res = await createCertificationType({ name });
      if (res.success && res.data) {
        onTypeCreated(res.data);
        onChange(res.data.id);
        setOpen(false);
        setSearch('');
      } else {
        setAddError('Could not add certification type');
      }
    } catch {
      setAddError('Could not add certification type');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between border rounded-md px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white transition-colors border-neutral-300 hover:border-neutral-400 ${
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span className={selected ? 'text-neutral-900' : 'text-neutral-400'}>
          {selected ? typeLabel(selected) : 'Select…'}
        </span>
        <ChevronDown className={`h-4 w-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg">
          {/* Search */}
          <div className="p-2 border-b border-neutral-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && showAddNew) { e.preventDefault(); handleAddNew(); }
                if (e.key === 'Escape') { setOpen(false); setSearch(''); }
              }}
              placeholder={canAddType ? 'Search or type a new type…' : 'Search…'}
              className="w-full text-sm px-2 py-1.5 border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Options list */}
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && !showAddNew && (
              <p className="text-sm text-neutral-400 text-center py-4">
                {search.trim()
                  ? (canAddType ? 'No match — press Enter to add' : 'No match')
                  : 'No certification types yet'}
              </p>
            )}

            {filtered.map(t => (
              <button
                key={t.id}
                type="button"
                onMouseDown={() => handleSelect(t)}
                className="w-full flex items-center justify-between text-left px-3 py-2 text-sm text-neutral-800 hover:bg-primary-50 hover:text-primary-700"
              >
                <span>{typeLabel(t)}</span>
                {t.id === value && <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />}
              </button>
            ))}

            {/* Add new option */}
            {showAddNew && (
              <button
                type="button"
                onMouseDown={handleAddNew}
                disabled={adding}
                className="w-full flex items-center gap-1.5 text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 border-t border-neutral-100 font-medium"
              >
                {adding
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Plus className="h-3.5 w-3.5" />
                }
                {adding ? 'Adding…' : <>Add "<span>{search.trim()}</span>"</>}
              </button>
            )}
          </div>

          {addError && (
            <p className="text-xs text-red-600 px-3 py-2 border-t border-neutral-100">{addError}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default CertificationTypeCombobox;
